import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { usePedidosStore, ordenarPedidos } from './store';
import { COLUNAS, colunaDePedido, statusDeColuna } from './statusMap';
import type { ColunaKanban } from './types';
import KanbanColumn from './KanbanColumn';
import PedidoCard from './PedidoCard';
import { useToast } from '@/components/Toast';

const emptyPorColuna: Record<ColunaKanban, string> = {
  aguardando_pagamento: 'Sem pedidos aguardando pagamento.',
  pago: 'Nenhum pedido pago.',
  aguardando_separacao: 'Nenhum pedido separado.',
};

interface BoardProps {
  filtro?: string;
}

export function KanbanBoard({ filtro = '' }: BoardProps) {
  const pedidosMap = usePedidosStore((s) => s.pedidos);
  const pedidos = useMemo(() => {
    const ordenados = ordenarPedidos(pedidosMap);
    const t = filtro.trim().toLowerCase();
    if (!t) return ordenados;
    return ordenados.filter((p) => {
      const hay = `${p.nome_cliente} ${p.numero_ticket} ${p.endereco_entrega}`.toLowerCase();
      return hay.includes(t);
    });
  }, [pedidosMap, filtro]);
  const mudarStatus = usePedidosStore((s) => s.mudarStatus);
  const selecionar = usePedidosStore((s) => s.selecionar);
  const toast = useToast();

  const [arrastando, setArrastando] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const porColuna = useMemo(() => {
    const buckets: Record<ColunaKanban, typeof pedidos> = {
      aguardando_pagamento: [],
      pago: [],
      aguardando_separacao: [],
    };
    for (const p of pedidos) {
      const c = colunaDePedido(p);
      if (c) buckets[c].push(p);
    }
    return buckets;
  }, [pedidos]);

  const onDragStart = (e: DragStartEvent) => {
    const id = e.active.data.current?.pedidoId;
    if (typeof id === 'number') setArrastando(id);
  };

  const onDragEnd = async (e: DragEndEvent) => {
    setArrastando(null);
    const id = e.active.data.current?.pedidoId as number | undefined;
    const coluna = e.over?.data.current?.coluna as ColunaKanban | undefined;
    if (typeof id !== 'number' || !coluna) return;

    const pedido = usePedidosStore.getState().pedidos[id];
    if (!pedido) return;
    const colunaAtual = colunaDePedido(pedido);
    if (colunaAtual === coluna) return;

    try {
      await mudarStatus(id, statusDeColuna(coluna));
      const titulo = COLUNAS.find((c) => c.id === coluna)?.titulo ?? coluna;
      toast('success', `${pedido.numero_ticket} → ${titulo}.`);
    } catch (err) {
      toast('error', `Falha ao mover ${pedido.numero_ticket}.`);
      // store já fez rollback
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const arrastandoPedido = arrastando ? usePedidosStore.getState().pedidos[arrastando] : null;
  const arrastandoColuna = arrastandoPedido ? colunaDePedido(arrastandoPedido) : null;
  const arrastandoAcento =
    arrastandoColuna && (COLUNAS.find((c) => c.id === arrastandoColuna)?.acento ?? 'moss');

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setArrastando(null)}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-s5 flex-1 min-h-0">
        {COLUNAS.map((col) => (
          <KanbanColumn
            key={col.id}
            {...col}
            count={porColuna[col.id].length}
            empty={emptyPorColuna[col.id]}
          >
            {porColuna[col.id].map((p) => (
              <PedidoCard
                key={p.id}
                pedido={p}
                acento={col.acento}
                onClick={() => selecionar(p.id)}
              />
            ))}
          </KanbanColumn>
        ))}
      </div>

      <DragOverlay dropAnimation={{ duration: 480, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
        {arrastandoPedido && arrastandoAcento && (
          <PedidoCard pedido={arrastandoPedido} acento={arrastandoAcento} overlay />
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default KanbanBoard;
